/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import gql from 'graphql-tag';
import { compact } from 'lodash';
import { compose } from 'recompose';

import { Typography } from '@coursera/cds-core';

import {
  buildCertificatesUrlRelative,
  buildDegreeUrlAbsolute,
  buildMastertrackUrlAbsolute,
} from 'bundles/common/utils/urlUtils';
import withFragments from 'bundles/graphql/components/withFragments';
import FullWidthCard from 'bundles/partners-graphql/components/FullWidthCard';
import type { PartnerPageQuery_PartnersV1Resource_shortName_partners_mastertracks as Mastertrack } from 'bundles/partners-graphql/components/__generated__/PartnerPageQuery';
import { org_coursera_catalogp_degree_CatalogAvailabilityStatus as AVAILABILITY_STATUS } from 'bundles/partners-graphql/components/__generated__/globalTypes';
import { masterTrackVariants } from 'bundles/partners-graphql/constants';
import type { PartnersPremiumProductCollections_LearningProductFragment } from 'bundles/partners-graphql/queries/__generated__/GetAllDegrees';

import _t from 'i18n!nls/partners-graphql';

const styles = {
  degreesUpsell: css`
    padding: 15px;
  `,
  degreesUpsellHeader: css`
    margin-bottom: 16px !important;
  `,
};

type Props = {
  degrees: PartnersPremiumProductCollections_LearningProductFragment[] | null;
  mastertracks: Mastertrack | null;
};

export const DegreeCards = ({ degrees, mastertracks }: Props) => {
  const availableMastertracks = mastertracks?.elements.filter(
    (mastertrack) => mastertrack?.catalogAvailabilityStatus !== AVAILABILITY_STATUS.ARCHIVED
  );

  const availableDegrees =
    degrees?.filter((degree) => degree?.catalogAvailabilityStatus !== AVAILABILITY_STATUS.ARCHIVED) ?? [];

  const degreeOfferings = availableDegrees?.map((degree) => {
    if (!degree || !degree.thumbnailUrl) {
      return null;
    }

    return {
      id: degree.id,
      name: degree.name,
      subtitle: degree.tagline,
      iconSrc: degree.thumbnailUrl,
      link: buildDegreeUrlAbsolute(degree.slug),
    };
  });
  const mastertrackOfferings = availableMastertracks?.map((mastertrack) => {
    if (!mastertrack || !mastertrack.thumbnail) {
      return null;
    }

    // Other certificate programs are returned in this response, but all the other
    // ones use /certificate in their links
    const programLink =
      mastertrack.productVariant === masterTrackVariants.Mastertrack
        ? buildMastertrackUrlAbsolute(mastertrack.slug)
        : buildCertificatesUrlRelative(mastertrack.slug);

    return {
      id: mastertrack.id,
      name: mastertrack.name,
      subtitle: mastertrack.tagline,
      iconSrc: mastertrack.thumbnail,
      link: programLink,
    };
  });

  const allOfferings = [...compact(degreeOfferings), ...compact(mastertrackOfferings)];

  if (allOfferings.length === 0) return null;

  return (
    <div className="rc-DegreesUpsell" css={styles.degreesUpsell}>
      <Typography css={styles.degreesUpsellHeader} variant="h2" component="h3">
        {_t('Degrees')}
      </Typography>
      {allOfferings.map((offering) => (
        <FullWidthCard
          key={offering.id}
          name={offering.name}
          logo={offering.iconSrc}
          slug={offering.link}
          tagline={offering.subtitle}
        />
      ))}
    </div>
  );
};

export const degreesUpsellMastertracksFragment = gql`
  fragment MastertracksFragment on CatalogMastertracksV1Connection {
    elements {
      id
      slug
      name
      productVariant
      tagline
      thumbnail
      catalogAvailabilityStatus
    }
  }
`;

export default compose<Props, Props>(
  withFragments({
    mastertracks: degreesUpsellMastertracksFragment,
  })
)(DegreeCards);
